import { useRef, useState } from 'react';
import { UMButton, UMIcon } from '../../urbanmap/_components';
import { downloadRatioTemplate, parseRatioFile, parseRatioWorkbook, type ImportResult } from './importRatios';
import { PC, UMR, caption } from './tokens';
import type { DegreeDays, RatioTable as Ratios } from './types';

interface Props {
  degrees: DegreeDays;
  ratios: Ratios;
  onImport: (degrees: DegreeDays, ratios: Ratios) => void;
  disabled?: boolean;
  /** Masque le rappel de format (boutons alignés avec les autres actions). */
  compact?: boolean;
}

type Note = { tone: 'ok' | 'error'; text: string; detail?: string[] };

/** Import d'un fichier de ratios (export tableur) et modèle pré-rempli. */
export function RatioImport({ degrees, ratios, onImport, disabled, compact }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [reading, setReading] = useState(false);

  const read = async (file: File) => {
    setReading(true);
    setNote(null);
    let r: ImportResult;
    try {
      r = /\.xlsx?$/i.test(file.name)
        ? parseRatioWorkbook(await file.arrayBuffer(), { degrees, ratios })
        : parseRatioFile(await file.text(), { degrees, ratios });
    } catch (error) {
      setNote({ tone: 'error', text: `Impossible de lire « ${file.name} » : ${error instanceof Error ? error.message : 'fichier illisible'}. Utilisez le modèle Excel proposé.` });
      return;
    } finally {
      setReading(false);
    }
    if (r.applied === 0 && !r.djuFound && !r.djclimFound) {
      setNote({ tone: 'error', text: `Aucune donnée reconnue dans « ${file.name} ». Partez du modèle pour retrouver le format attendu.`, detail: r.skipped.slice(0, 4) });
      return;
    }
    onImport(r.degrees, r.ratios);
    const parts = [`${r.applied} ratio${r.applied > 1 ? 's' : ''} importé${r.applied > 1 ? 's' : ''}`];
    if (r.djuFound) parts.push('DJU repris');
    if (r.djclimFound) parts.push('DJClim repris');
    if (r.skipped.length) parts.push(`${r.skipped.length} ligne(s) ignorée(s)`);
    setNote({ tone: 'ok', text: parts.join(' · '), detail: r.skipped.slice(0, 4) });
  };

  const download = () => {
    try {
      downloadRatioTemplate(degrees, ratios);
    } catch (error) {
      setNote({ tone: 'error', text: `Le modèle Excel n’a pas pu être généré : ${error instanceof Error ? error.message : 'erreur de téléchargement'}.` });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <UMButton variant="secondary" icon="upload_file" disabled={disabled || reading} onClick={() => fileRef.current?.click()}>
          {reading ? 'Lecture du fichier…' : 'Importer un fichier de ratios'}
        </UMButton>
        <UMButton variant="subtle" icon="download" disabled={disabled || reading} onClick={download}>Modèle Excel pré-rempli (.xlsx)</UMButton>
        {!compact && <span style={caption}>Excel (.xlsx, .xls), CSV ou TSV · Typologie, Chauffage, ECS, Froid, DJU et DJClim.</span>}
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt,.xlsx,.xls"
          disabled={disabled || reading}
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); e.target.value = ''; }}
        />
      </div>
      {note && (
        <div role={note.tone === 'error' ? 'alert' : 'status'} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: 8, fontSize: 13, background: note.tone === 'ok' ? PC.accentBg : PC.dangerBg, border: `1px solid ${note.tone === 'ok' ? PC.accentBorder : PC.dangerBorder}`, color: note.tone === 'ok' ? UMR.black : PC.dangerText }}>
          <UMIcon name={note.tone === 'ok' ? 'check_circle' : 'error'} size={18} color={note.tone === 'ok' ? PC.accent : PC.dangerText} />
          <span>
            {note.text}
            {note.detail && note.detail.length > 0 && (
              <span style={{ ...caption, display: 'block' }}>Non reprises : {note.detail.join(' · ')}</span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}
